/**
 * Products and stock — the `/v1/admin/products` and `/v1/admin/stock` client.
 *
 * Separate from `catalogue.ts`, which reads the **public** endpoints. The split
 * mirrors the API's own: the public surface carries two booleans where this one
 * carries the exact count, and DRAFT and ARCHIVED products are invisible there
 * and visible here (PLAN.md §4.2.1). Two files, each honest about the surface
 * it speaks to, is what keeps a screen from accidentally rendering a storefront
 * response and quietly showing "In stock" where a number was needed.
 */
import { apiFetch, uploadImage } from "@/lib/api-client";
import type {
  AdminProduct,
  AdminProductDetail,
  AdminStockMovement,
  ApiResponse,
  Paginated,
  ProductColour,
  ProductStatus,
  StockAdjustment,
  StockReason,
  StockSummary,
} from "@/lib/api/types";

/** Denser than the storefront's page size — these are tables, not showcases. */
export const PER_PAGE = 25;

type Query = Record<string, string | number | boolean | undefined>;

function clean(query: Query): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== undefined && value !== ""),
  ) as Record<string, string | number | boolean>;
}

const path = (slug: string, suffix = "") =>
  `/v1/admin/products/${encodeURIComponent(slug)}${suffix}`;

// ─── Reading ─────────────────────────────────────────────────────────────────

export type ProductQuery = {
  page?: number;
  status?: ProductStatus | undefined;
  category?: string | undefined;
  search?: string | undefined;
  /** "low" is everything at or below its own threshold; "out" is zero. */
  stock?: "low" | "out" | undefined;
  sort?: "position" | "stock" | "name" | "updated" | undefined;
};

export function fetchProducts(
  query: ProductQuery = {},
  signal?: AbortSignal,
): Promise<Paginated<AdminProduct[]>> {
  return apiFetch<Paginated<AdminProduct[]>>("/v1/admin/products", {
    query: clean({
      page: query.page ?? 1,
      limit: PER_PAGE,
      status: query.status,
      category: query.category,
      search: query.search,
      stock: query.stock,
      sort: query.sort,
    }),
    ...(signal ? { signal } : {}),
  });
}

export async function fetchProduct(
  slug: string,
  signal?: AbortSignal,
): Promise<AdminProductDetail> {
  const { data } = await apiFetch<ApiResponse<AdminProductDetail>>(path(slug), {
    ...(signal ? { signal } : {}),
  });

  return data;
}

// ─── Editing ─────────────────────────────────────────────────────────────────

/**
 * The editable fields.
 *
 * `slug` is absent because the storefront's pages are baked from it, `price`
 * because a price change owes a `PriceHistory` row and has its own call, and
 * `stockQuantity` because `adjustStock` is the only thing allowed to write it.
 * The API drops all three; leaving them out of the type means the form never
 * offers them in the first place.
 */
export type ProductEdit = {
  name?: string;
  eyebrow?: string;
  spec?: string;
  badge?: string;
  description?: string;
  specSheet?: string;
  status?: ProductStatus;
  position?: number;
  homePosition?: number | null;
  lowStockThreshold?: number;
  category?: string;
  /**
   * Tax fields. Both nullable so a code entered in error can be taken back
   * out — a product with no HSN cannot be invoiced, which is the correct and
   * visible consequence, where a wrong code left in place is invisible.
   *
   * Changing either affects future orders only. Every order already placed
   * carries its own frozen copy.
   */
  hsnCode?: string | null;
  gstRateBp?: number | null;
  /**
   * Weight in grams and the packed carton in millimetres.
   *
   * All three dimensions together or all three cleared — the API refuses a
   * partial set, because a carton with two sides is not a carton and the
   * missing one would be read as zero when the volumetric weight is computed.
   */
  weightGrams?: number | null;
  lengthMm?: number | null;
  breadthMm?: number | null;
  heightMm?: number | null;
};

export async function updateProduct(slug: string, edit: ProductEdit): Promise<AdminProductDetail> {
  const { data } = await apiFetch<ApiResponse<AdminProductDetail>>(path(slug), {
    method: "PATCH",
    body: edit,
  });

  return data;
}

/**
 * A price change. Appends to `PriceHistory` — the old price is never lost.
 *
 * `discountLabel` is required rather than derived, and that is not an
 * oversight: the distributor sheet labels every line "25% off" where the MRP
 * arithmetic gives 20%, and the storefront carries the sheet's number verbatim.
 * Computing it would silently change every price chip on the site.
 */
export async function updatePrice(
  slug: string,
  input: { priceMinor: number; mrpMinor: number; discountLabel: string; note?: string },
): Promise<AdminProductDetail> {
  const { data } = await apiFetch<ApiResponse<AdminProductDetail>>(path(slug, "/price"), {
    method: "PATCH",
    body: clean(input),
  });

  return data;
}

// ─── Stock ───────────────────────────────────────────────────────────────────

/**
 * Adjust inventory.
 *
 * Exactly one of `delta` and `countedQuantity` — the API rejects both together,
 * and the form enforces the same thing so the rejection is never how an
 * operator finds out.
 *
 * **`countedQuantity` is not "set stock to N".** The API computes the delta
 * inside the transaction against the balance at that instant, so a sale that
 * lands while the form is open is preserved rather than erased. Which means the
 * returned `balance` may not equal what was counted — and when it does not,
 * that is the correct answer and worth showing.
 */
export type StockAdjustInput = {
  delta?: number;
  countedQuantity?: number;
  reason: Extract<StockReason, "RESTOCK" | "CORRECTION">;
  note?: string;
};

export async function adjustStock(slug: string, input: StockAdjustInput): Promise<StockAdjustment> {
  const { data } = await apiFetch<ApiResponse<StockAdjustment>>(path(slug, "/stock"), {
    method: "PATCH",
    body: clean(input as Query),
  });

  return data;
}

export type StockLog = Paginated<AdminStockMovement[]> & {
  product: { slug: string; name: string };
};

export function fetchStockLog(
  slug: string,
  query: { page?: number; reason?: StockReason | undefined } = {},
  signal?: AbortSignal,
): Promise<StockLog> {
  return apiFetch<StockLog>(path(slug, "/stock/log"), {
    query: clean({ page: query.page ?? 1, limit: PER_PAGE, reason: query.reason }),
    ...(signal ? { signal } : {}),
  });
}

export function fetchLowStock(
  query: { page?: number; includeArchived?: boolean } = {},
  signal?: AbortSignal,
): Promise<Paginated<AdminProduct[]>> {
  return apiFetch<Paginated<AdminProduct[]>>("/v1/admin/stock/low", {
    query: clean({
      page: query.page ?? 1,
      limit: PER_PAGE,
      includeArchived: query.includeArchived ? "true" : undefined,
    }),
    ...(signal ? { signal } : {}),
  });
}

export async function fetchStockSummary(signal?: AbortSignal): Promise<StockSummary> {
  const { data } = await apiFetch<ApiResponse<StockSummary>>("/v1/admin/stock/summary", {
    ...(signal ? { signal } : {}),
  });

  return data;
}

// ─── Creating (ADMIN.md §9, phase 5c) ────────────────────────────────────────

/**
 * Everything a product needs to exist.
 *
 * Wider than `ProductEdit` on purpose, and the difference is the point. An edit
 * is a correction to a row that already renders, so any subset is meaningful. A
 * create has nothing behind it: a product with no `spec` draws an empty line on
 * every card in its category, and the operator who left it blank is not the one
 * who will notice.
 *
 * `stockQuantity` is still absent — a new product starts at zero and takes its
 * opening stock through the Stock screen, so the ledger reconciles from the
 * first unit rather than the second.
 */
export type ProductCreate = {
  slug: string;
  name: string;
  eyebrow: string;
  spec: string;
  category: string;
  priceMinor: number;
  mrpMinor: number;
  discountLabel: string;
  badge?: string;
  description: string;
  specSheet: string;
  status?: ProductStatus;
  homePosition?: number | null;
  lowStockThreshold?: number;
  hsnCode?: string | null;
  gstRateBp?: number | null;
  weightGrams?: number | null;
  lengthMm?: number | null;
  breadthMm?: number | null;
  heightMm?: number | null;
  colours?: ProductColour[];
  features?: string[];
};

export async function createProduct(input: ProductCreate): Promise<AdminProductDetail> {
  const { data } = await apiFetch<ApiResponse<AdminProductDetail>>("/v1/admin/products", {
    method: "POST",
    body: input,
  });

  return data;
}

/**
 * Turns a product name into a slug the API will accept.
 *
 * Offered as a starting point in the create form, never applied silently: the
 * slug is the product's permanent URL and is not editable afterwards, so the
 * operator has to see and accept it. Mirrors the API's own pattern — lowercase,
 * digits, single hyphens — and a name that reduces to nothing leaves the field
 * empty rather than producing something meaningless.
 */
export function slugify(name: string): string {
  return (
    name
      .toLowerCase()
      .normalize("NFD")
      // Strip accents rather than dropping the letters they sit on: "Café" should
      // become "cafe", not "caf".
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 120)
      .replace(/-+$/, "")
  );
}

// ─── Images ──────────────────────────────────────────────────────────────────

/**
 * Uploads one photograph.
 *
 * The bytes go through `uploadImage`, which seals them into the binary frame —
 * not multipart, because a multipart body puts the filename in a plaintext
 * header, which is one of the things the frame exists to hide.
 *
 * **Alt text goes inside the frame, not the query string.** It was a query
 * parameter first, and that failed twice over: `uploadImage` keeps only the
 * path, so it never arrived at all; and even if it had, a query parameter sits
 * outside the ciphertext where it is neither hidden nor covered by the AEAD
 * tag. Sealed with the bytes it describes is the correct place for it.
 *
 * The API re-encodes whatever is sent to bounded WebP, so there is no need to
 * convert or resize here. Do check `file.size` before calling: the server will
 * refuse an oversized upload, but only after the browser has spent the upload
 * discovering that.
 */
export async function addImage(slug: string, file: File, alt: string): Promise<AdminProductDetail> {
  const { data } = await uploadImage<ApiResponse<AdminProductDetail>>(path(slug, "/images"), {
    metadata: { filename: file.name, contentType: file.type, alt },
    bytes: new Uint8Array(await file.arrayBuffer()),
  });

  return data;
}

export async function updateImage(
  slug: string,
  id: string,
  alt: string,
): Promise<AdminProductDetail> {
  const { data } = await apiFetch<ApiResponse<AdminProductDetail>>(
    `${path(slug, "/images/")}${encodeURIComponent(id)}`,
    { method: "PATCH", body: { alt } },
  );

  return data;
}

/** The full gallery, in the order it should appear. Position 0 is the cover. */
export async function reorderImages(slug: string, order: string[]): Promise<AdminProductDetail> {
  const { data } = await apiFetch<ApiResponse<AdminProductDetail>>(path(slug, "/images/order"), {
    method: "PATCH",
    body: { order },
  });

  return data;
}

export async function deleteImage(slug: string, id: string): Promise<AdminProductDetail> {
  const { data } = await apiFetch<ApiResponse<AdminProductDetail>>(
    `${path(slug, "/images/")}${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );

  return data;
}

// ─── Finishes and feature tags ───────────────────────────────────────────────

/** Replaces the whole set — see the `PUT` on the API side. */
export async function replaceColours(
  slug: string,
  colours: ProductColour[],
): Promise<AdminProductDetail> {
  const { data } = await apiFetch<ApiResponse<AdminProductDetail>>(path(slug, "/colours"), {
    method: "PUT",
    body: { colours },
  });

  return data;
}

export async function replaceFeatures(
  slug: string,
  features: string[],
): Promise<AdminProductDetail> {
  const { data } = await apiFetch<ApiResponse<AdminProductDetail>>(path(slug, "/features"), {
    method: "PUT",
    body: { features },
  });

  return data;
}

/** The tag vocabulary. `replaceFeatures` accepts nothing outside it. */
export async function fetchFeatures(signal?: AbortSignal): Promise<string[]> {
  const { data } = await apiFetch<ApiResponse<{ label: string }[]>>("/v1/admin/features", {
    ...(signal ? { signal } : {}),
  });

  return data.map((entry) => entry.label);
}
